import { toast } from "@overmap-ai/blocks"
import {
	type Issue,
	type IssueTypePriority,
	type IssueTypeStatus,
	type Stored,
	type User,
	ERROR_MSG_UNEXPECTED,
} from "@overmap-ai/common"
import {
	selectIssueTypeById,
	selectIssueTypePriorityById,
	selectIssueTypeStatusById,
	selectUserById,
} from "@overmap-ai/core"
import type { JSONContent } from "@tiptap/react"
import { useCallback, useMemo } from "react"

import { sdk } from "../sdk"
import { useAppSelector } from "../store"
import {
	selectAttachmentsByIssueTypeFieldValues,
	selectIssueAssociationsByIssue,
	selectIssueTypeIdentifierValuesByIssue,
	selectLatestIssueTypeFieldValuesByIssue,
} from "../store/selectors/issues"
import type { IssueAndIdentifiers, IssueData } from "../typings"
import { useConfirmDeleteDialog } from "./utils"

export const useIssueData = (issues: Stored<Issue>[]) => {
	const issueTypeIdentifierValuesByIssue = useAppSelector(selectIssueTypeIdentifierValuesByIssue)
	const issueTypeFieldValuesByIssue = useAppSelector(selectLatestIssueTypeFieldValuesByIssue)
	const attachmentsByIssueTypeFieldValues = useAppSelector(selectAttachmentsByIssueTypeFieldValues)
	const issueAssociationsByIssue = useAppSelector(selectIssueAssociationsByIssue)

	return useMemo(() => {
		const ret: IssueData[] = []

		for (const issue of issues) {
			const issueTypeIdentifierValues = issueTypeIdentifierValuesByIssue[issue.uuid] ?? {}
			const issueTypeFieldValues = issueTypeFieldValuesByIssue[issue.uuid]
			const issueTypeFieldValuesAttachments =
				attachmentsByIssueTypeFieldValues[issueTypeFieldValues?.uuid ?? ""] ?? []
			const issueAssociations = issueAssociationsByIssue[issue.uuid] ?? []

			ret.push({
				issue,
				issueTypeFieldValues,
				issueTypeFieldValuesAttachments,
				issueAssociations,
				issueTypeIdentifierValues,
			})
		}

		return ret
	}, [
		issueAssociationsByIssue,
		issueTypeFieldValuesByIssue,
		issueTypeIdentifierValuesByIssue,
		issues,
		attachmentsByIssueTypeFieldValues,
	])
}

/** custom hook  used to get common functionality related to an issue */
export const useIssue = (issue: Stored<Issue>) => {
	const assignee = useAppSelector(selectUserById(issue.assigned_to ?? -1))
	const issueType = useAppSelector(selectIssueTypeById(issue.issue_type))!
	const issueTypePriority = useAppSelector(selectIssueTypePriorityById(issue.priority))!
	const issueTypeStatus = useAppSelector(selectIssueTypeStatusById(issue.status))!

	const handleTitleChange = useCallback(
		(title: string) => {
			return sdk.issues.update({ uuid: issue.uuid, title: title })
		},
		[issue.uuid],
	)

	const handleDescriptionChange = useCallback(
		(content: JSONContent | undefined) => {
			return sdk.issues.update({
				uuid: issue.uuid,
				description: content ? JSON.stringify(content) : undefined,
			})
		},
		[issue.uuid],
	)

	const handlePriorityChange = useCallback(
		(priority: Stored<IssueTypePriority>) => {
			return sdk.issues.update({ uuid: issue.uuid, priority: priority.uuid })
		},
		[issue.uuid],
	)

	const handleStatusChange = useCallback(
		(status: Stored<IssueTypeStatus>) => {
			return sdk.issues.update({ uuid: issue.uuid, status: status.uuid })
		},
		[issue.uuid],
	)

	const handleAssigneeChange = useCallback(
		(user: User | null) => {
			return sdk.issues.update({ uuid: issue.uuid, assigned_to: user ? user.id : null })
		},
		[issue.uuid],
	)

	const handleDueDateChange = useCallback(
		(dueDate: Date | undefined) => {
			return sdk.issues.update({ uuid: issue.uuid, due_date: dueDate ? dueDate.toISOString() : null })
		},
		[issue.uuid],
	)

	return useMemo(
		() => ({
			issueType,
			assignee,
			issueTypePriority,
			issueTypeStatus,
			handleTitleChange,
			handleDescriptionChange,
			handlePriorityChange,
			handleStatusChange,
			handleAssigneeChange,
			handleDueDateChange,
		}),
		[
			issueType,
			assignee,
			handleTitleChange,
			handleDescriptionChange,
			handlePriorityChange,
			handleStatusChange,
			handleAssigneeChange,
			handleDueDateChange,
			issueTypePriority,
			issueTypeStatus,
		],
	)
}

export const useDeleteIssue = () => {
	const openConfirmDeleteDialog = useConfirmDeleteDialog()

	return useCallback(
		(issueId: Issue["uuid"], onConfirm?: () => void | Promise<void>) => {
			openConfirmDeleteDialog({
				title: "Delete issue",
				description: "Are you sure you want to delete this issue? This cannot be undone.",
				onAction: async () => {
					if (onConfirm) await onConfirm()

					try {
						return await sdk.issues.delete(issueId)
					} catch (e: unknown) {
						toast.error({
							title: "Failed to delete the issue.",
							description: e instanceof Error ? e.message : ERROR_MSG_UNEXPECTED,
						})
					}
				},
			})
		},
		[openConfirmDeleteDialog],
	)
}

export const useIssuesWithIdentifiers = (issues: Stored<Issue>[]): IssueAndIdentifiers[] => {
	const issueTypeIdentifierValuesByIssue = useAppSelector(selectIssueTypeIdentifierValuesByIssue)

	return useMemo(() => {
		return issues.map((issue) => ({
			issue,
			identifierValues: Object.values(issueTypeIdentifierValuesByIssue[issue.uuid] ?? {}),
		}))
	}, [issueTypeIdentifierValuesByIssue, issues])
}
